/* eslint-disable @typescript-eslint/no-explicit-any */
// todo: Add correct typing

import type { FC } from 'react'
import { useState } from 'react'

import { Swipeable } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import BackIcon from 'mdi-react/ArrowLeftIcon'
import CancelIcon from 'mdi-react/BackspaceIcon'
import SaveIcon from 'mdi-react/CheckIcon'

import { postApplication } from '@/api/salaryDeduction'
import { useAppContext } from '@/App.context'
import { LocalDate } from '@/components/LocalDate'
import useSetup from '@/hooks/setup'
import useUser from '@/hooks/user'
import { numberFormat } from '@/util/formatters/numbers'

import { useTranslation } from '../i18n/hooks'
import { Translation } from '../i18n/Translation'

import { analyzePost } from './util'

import './style/salary-deduction-application.scss'

const ContentServices: FC<{ applicationPost: any; contentServices: any }> = ({
  applicationPost,
  contentServices,
}) => {
  if (applicationPost.p_id !== 1844 || !contentServices) return null

  return contentServices.map(
    ({ id, sum_inc_tax, time, date, card_number, service, support_number, vendor }: any) => (
      <div className="cdr" key={id}>
        <div className="amt">{numberFormat(parseInt(sum_inc_tax.trim()), 2)}</div>
        <div className="datetime">
          <LocalDate date={date} />
          {' - '}
          {time}
        </div>
        <div className="product">{service}</div>
        <div className="to-number">
          › {card_number} - {vendor} - {support_number}
        </div>
      </div>
    )
  )
}

const Warning: FC<{ warning: any }> = ({ warning }) => {
  if (!warning) return null

  return <div className="warning">{warning}</div>
}

const CDRs: FC<{ applicationPost: any; CDRs: any }> = ({ applicationPost, CDRs }) => {
  const re1 = /varer og tjenester/i
  const re2 = /sms tjenester/i
  const re3 = /Til 3-, 4- og 5-sifrede nummer/i // p_id 747
  const re4 = /SMS til 4- og 5-sifrede nummer/i // p_id 690

  const isCDRsVisible =
    (re1.test(applicationPost.product) ||
      re2.test(applicationPost.product) ||
      applicationPost.p_id === 747 ||
      applicationPost.p_id === 690) &&
    CDRs

  if (!isCDRsVisible) return null
  return CDRs.map(({ id, VatBaseAmount, Date, Time, ProdName, ToNumber }: any) => {
    const re = new RegExp(ProdName, 'i')
    return (
      (re.test(applicationPost.product) ||
        (applicationPost.p_id === 747 && re3.test(ProdName)) ||
        (applicationPost.p_id === 690 && re4.test(ProdName))) && (
        <div className="cdr" key={id}>
          <div className="amt">{numberFormat(VatBaseAmount * 1.25, 2)}</div>
          <div className="datetime">
            <LocalDate date={Date} />
            {' - '}
            {Time}
          </div>
          <div className="product">{ProdName}</div>
          <div className="to-number">› {ToNumber}</div>
        </div>
      )
    )
  })
}

const SalaryDeduction: FC<{
  status: any
  value: any
  onChange: any
  setToZero: any
  applicationPost: any
}> = ({ status, value, onChange, setToZero, applicationPost }) => {
  if (status?.applicable) {
    return (
      <span>
        <input
          value={value}
          onChange={onChange}
          type="number"
          min={0}
          max={applicationPost.amount}
        />
        <Button variant="primary" size="small" onClick={setToZero}>
          <Translation id="Total amount" />
        </Button>
      </span>
    )
  }

  return (
    <span>
      {status.code === 0
        ? numberFormat(applicationPost.sda_amount + applicationPost.sda_vat, 2)
        : numberFormat(value, 2)}
    </span>
  )
}

const Comment: FC<{
  status: any
  applicationText: any
  changeApplicationText: any
  applicationPost: any
}> = ({ status, applicationText, changeApplicationText, applicationPost }) => {
  const translate = useTranslation()
  if (status.applicable) {
    return (
      <textarea
        placeholder={translate(
          'Please enter the amount you wish to have covered above and enter a comment here.'
        )}
        value={applicationText}
        onChange={changeApplicationText}
      />
    )
  }

  return (
    <div className="info-block">
      <div className="label comment">
        <Translation id="Comment" />:
      </div>
      <div className="value">
        {applicationPost.sda_comment && applicationPost.sda_comment.length > 0 ? (
          applicationPost.sda_comment
        ) : (
          <Translation id="No comment." />
        )}
      </div>
    </div>
  )
}

const Content = ({
  applicationPost,
  freeAllowanceLock,
  hasDeduction,
  setUp,
  deadLinePassed,
  getHeader,
  invoice,
  contentServices,
  cdrs,
  salaryDeductionValue,
  setToZero,
  changeApplicationText,
  save,
  loading,
  closeApplication,
  changeSalaryDeductionValue,
  applicationText,
}: any) => {
  if (!applicationPost) return null
  const application = analyzePost(
    applicationPost,
    freeAllowanceLock,
    hasDeduction,
    setUp.private_active,
    deadLinePassed
  )

  const colorClassName = 'color color' + application.color

  const saveButton = (status: any) => {
    if (status.applicable) {
      return (
        <Button variant="primary" onClick={save} Icon={<SaveIcon />}>
          <Translation id="Save" />
        </Button>
      )
    }
  }
  return (
    <div className="content-wrapper">
      {getHeader(invoice)}

      <div
        className="header"
        onClick={closeApplication}
        onKeyDown={closeApplication}
        role="button"
        tabIndex={0}
      >
        {applicationPost.product}
      </div>

      <Warning warning={application.warning} />

      <div className="info-block">
        <div className="label">
          <Translation id="Status" />:
        </div>
        <div className="value">
          <div className={colorClassName}>{application.text}</div>
        </div>
      </div>
      <div className="info-block">
        <div className="label">
          <Translation id="Amount" />:
        </div>
        <div className="value">{numberFormat(applicationPost.amount + applicationPost.vat, 2)}</div>
      </div>
      <div className="info-block">
        <div className="label">
          <Translation id="Covered amount" />:
        </div>
        <div className="value">
          {' '}
          <SalaryDeduction
            status={application}
            value={salaryDeductionValue}
            onChange={changeSalaryDeductionValue}
            setToZero={setToZero}
            applicationPost={applicationPost}
          />
        </div>
      </div>

      <Comment
        status={application}
        applicationText={applicationText}
        changeApplicationText={changeApplicationText}
        applicationPost={applicationPost}
      />

      {loading ? (
        <div className="loader dark" />
      ) : (
        <div className="buttons">
          {saveButton(application)}

          <Button
            onClick={closeApplication}
            Icon={application.applicable ? <CancelIcon /> : <BackIcon />}
          >
            {application.applicable ? <Translation id="Cancel" /> : <Translation id="Back" />}
          </Button>
        </div>
      )}
      <div className="cdrs">
        <CDRs applicationPost={applicationPost} CDRs={cdrs} />
      </div>
      <div className="content-services">
        <ContentServices applicationPost={applicationPost} contentServices={contentServices} />
      </div>
    </div>
  )
}

export const SalaryDeductionApplication = ({
  applicationPost,
  freeAllowanceLock,
  hasDeduction,
  deadLinePassed,
  getHeader,
  invoice,
  contentServices,
  CDRs,
  applicationMode,
  updatePost,
  closeApplication,
}: any) => {
  const [applicationText, setApplicationText] = useState('')
  const [salaryDeductionValue, setSalaryDeductionValue] = useState(0)
  const [sdaAmount, setSdaAmount] = useState(0)
  const [sdaVat, setSdaVat] = useState(0)
  const [loading, setLoading] = useState(false)
  const setup = useSetup()
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()
  const user = useUser()

  const changeApplicationText = (e: any) => {
    setApplicationText(e.target.value)
  }

  const setToZero = () => {
    const e = {
      target: { value: Math.round((applicationPost.amount + applicationPost.vat) * 100) / 100 },
    }
    changeSalaryDeductionValue(e)
  }

  const changeSalaryDeductionValue = (e: any) => {
    const newSalaryDeductionValue = e.target.value
    const ratio = applicationPost.vat ? applicationPost.vat / applicationPost.amount : 0
    let newSdaAmount = newSalaryDeductionValue / (1 + ratio)
    if (newSdaAmount > applicationPost.amount) newSdaAmount = applicationPost.amount
    setSalaryDeductionValue(newSalaryDeductionValue)
    setSdaAmount(newSdaAmount)
    setSdaVat(newSdaAmount * ratio)
  }

  const reset = () => {
    setApplicationText(' ')
    setSalaryDeductionValue(0)
    setSdaAmount(0)
    setSdaVat(0)
    setLoading(false)
  }

  const save = () => {
    if (!user) {
      return null
    }
    const payload = {
      irId: applicationPost.id,
      status: 0,
      amount: sdaAmount,
      vat: sdaVat,
      comment: applicationText,
      phoneNumber: user.code,
    }

    if (payload.amount <= 0) {
      alert(translate('The amount has to exceed zero!'))
      return null
    }
    if (payload.amount > applicationPost.amount + 1) {
      alert(translate('The covered amount can not exceed the initial amount!'))
      return null
    }
    if (payload.comment.length <= 8) {
      alert(translate('Please enter a comment in the comment field!'))
      return null
    }
    setLoading(true)
    // todo: migrate to react query

    postApplication(payload)
      .then(() => {
        updatePost(payload)
        closeApplication()
        setTimeout(reset, 500)
      })
      .catch(() => {})
  }

  return (
    <Swipeable
      id="salary-deduction-application"
      className={`${applicationMode ? 'is-visible' : 'is-hidden'} ${whiteLabel}`}
      onSwipedRight={closeApplication}
    >
      <Content
        applicationPost={applicationPost}
        freeAllowanceLock={freeAllowanceLock}
        hasDeduction={hasDeduction}
        setUp={setup}
        deadLinePassed={deadLinePassed}
        getHeader={getHeader}
        invoice={invoice}
        contentServices={contentServices}
        cdrs={CDRs}
        salaryDeductionValue={salaryDeductionValue}
        setToZero={setToZero}
        changeApplicationText={changeApplicationText}
        save={save}
        loading={loading}
        closeApplication={closeApplication}
        changeSalaryDeductionValue={changeSalaryDeductionValue}
        applicationText={applicationText}
        updatePost={updatePost}
        user={user}
      />
    </Swipeable>
  )
}
